/**
 * Balance Service
 *
 * API client for household balance and expense allocation endpoints.
 */

import { fetchApi } from '@/shared/services/api';
import type {
  ActivityFeedResponse,
  PaginatedResponse,
  PairUnsettledTransactionsResponse,
  RolloverLedgerResponse,
  SettlementPair,
  UnsettledTransactionItem,
  UserBalance,
  Money,
} from '@/shared/types';

export interface BalanceParams {
  start_date?: string;
  end_date?: string;
}

export interface BalanceSummary {
  period_start: string;
  period_end: string;
  user_balances: UserBalance[];
  total_expenses: Money;
  total_shared_expenses: Money;
}

export interface SettlementSuggestion {
  from_user_id: string;
  from_user_name: string;
  to_user_id: string;
  to_user_name: string;
  amount: Money;
}

export const balanceService = {
  /**
   * Get current balance summary for all household members
   */
  getSummary: (params?: BalanceParams) =>
    fetchApi<BalanceSummary>('/balances/summary', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get balances for all users
   */
  getUserBalances: (params?: BalanceParams) =>
    fetchApi<UserBalance[]>('/balances/users', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get balance for a specific user
   */
  getUserBalance: (userId: string, params?: BalanceParams) =>
    fetchApi<UserBalance>(`/balances/users/${userId}`, {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get suggested settlement transactions to balance accounts
   */
  getSettlementSuggestions: (params?: BalanceParams) =>
    fetchApi<SettlementSuggestion[]>('/balances/settlement-suggestions', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get chronological activity feed mixing shared expenses and settlements
   */
  getActivity: (params: BalanceParams & { page?: number; page_size?: number }) =>
    fetchApi<ActivityFeedResponse>('/balances/activity', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get transactions where the current user has an unsettled share
   */
  getUnsettledTransactions: (params: BalanceParams & { page?: number; page_size?: number }) =>
    fetchApi<PaginatedResponse<UnsettledTransactionItem>>('/balances/unsettled-transactions', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get settlement-focused rollover ledger with per-pair balance history
   */
  getRolloverLedger: (memberId?: string) =>
    fetchApi<RolloverLedgerResponse>('/balances/rollover-ledger', {
      params: memberId ? { member_id: memberId } : undefined,
    }),

  /**
   * Get bilateral balances between current user and each household member
   */
  getSettlementPairs: (params?: BalanceParams) =>
    fetchApi<SettlementPair[]>('/balances/settlement-pairs', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get unsettled transactions between current user and a specific other user
   */
  getPairUnsettledTransactions: (otherUserId: string, params?: BalanceParams) =>
    fetchApi<PairUnsettledTransactionsResponse>(
      '/balances/pair-unsettled-transactions',
      {
        params: {
          other_user_id: otherUserId,
          ...params,
        } as Record<string, unknown>,
      }
    ),
};
